import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { QrCode, User as UserIcon, MessageCircle, Ticket, ShoppingBag, Camera, Send, Inbox, Lock, Key } from 'lucide-react';
import { messagesService, ordersService, eventsService } from '../services/api-service';
import api from '../api/client';
import './Profile.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';

const Profile = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('details'); // 'details', 'messages', 'tickets', 'orders', 'photos'
  
  // Tickets state
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  
  // Messages state
  const [messages, setMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [newMessage, setNewMessage] = useState({ recipientId: '', subject: '', body: '' });
  const [sending, setSending] = useState(false);
  const [showComposer, setShowComposer] = useState(false);
  
  // Orders state
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  
  // Promotional code entry state (for Messages tab)
  const [promoCode, setPromoCode] = useState('');
  const [promoEventId, setPromoEventId] = useState('');
  const [promoCodeError, setPromoCodeError] = useState('');
  const [promoCodeSuccess, setPromoCodeSuccess] = useState('');
  const [validatingCode, setValidatingCode] = useState(false);

  // Load tickets
  const loadTickets = React.useCallback(async () => {
    if (!user) return;
    try {
      setTicketsLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/tickets/my?customerId=${user.id}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setTickets(data.data || []);
      }
    } catch (error) {
      console.error('Error loading tickets:', error);
    } finally {
      setTicketsLoading(false);
    }
  }, [user]);

  // Load messages/conversations
  const loadConversations = async () => {
    if (!user) return;
    try {
      setMessagesLoading(true);
      const data = await messagesService.getConversations();
      setConversations(data);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setMessagesLoading(false);
    }
  };

  const loadConversationMessages = async (conversationId) => {
    if (!user) return;
    try {
      const allMessages = await messagesService.getMyMessages();
      const conversationMessages = allMessages.filter(
        m => m.conversationId === conversationId || 
        (m.senderId === user.id && m.recipientId === selectedConversation?.otherUserId) ||
        (m.recipientId === user.id && m.senderId === selectedConversation?.otherUserId)
      );
      setMessages(conversationMessages.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)));
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  // Load orders
  const loadOrders = async () => {
    if (!user) return;
    try {
      setOrdersLoading(true);
      const data = await ordersService.getMyOrders();
      setOrders(data || []);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setOrdersLoading(false);
    }
  };

  // Load data when tab changes
  useEffect(() => {
    if (!user) return;
    
    if (activeTab === 'tickets') {
      loadTickets();
    } else if (activeTab === 'messages') {
      loadConversations();
    } else if (activeTab === 'orders') {
      loadOrders();
    }
  }, [activeTab, user, loadTickets]);

  useEffect(() => {
    if (selectedConversation && activeTab === 'messages') {
      loadConversationMessages(selectedConversation.id);
    }
  }, [selectedConversation, activeTab]);

  const loadTicketQRCode = async (ticketId) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/tickets/${ticketId}/qrcode?customerId=${user.id}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedTicket(data.data);
      }
    } catch (error) {
      console.error('Error loading QR code:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.recipientId || !newMessage.body) {
      alert('Please fill in all required fields');
      return;
    }

    setSending(true);
    try {
      await messagesService.sendMessage(newMessage);
      setNewMessage({ recipientId: '', subject: '', body: '' });
      setShowComposer(false);
      await loadConversations();
      alert('Message sent successfully!');
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleValidatePromoCode = async (e) => {
    e.preventDefault();
    if (!promoCode || !promoEventId) {
      setPromoCodeError('Please enter both event ID and promotional code');
      return;
    }

    setValidatingCode(true);
    setPromoCodeError('');
    setPromoCodeSuccess('');
    
    try {
      const result = await api.events.validateCode(promoEventId, promoCode);
      if (result.success && result.valid && result.accessGranted) {
        setPromoCodeSuccess('Code validated successfully! You now have access to this event.');
        setPromoCode('');
        setPromoEventId('');
        setTimeout(() => {
          setPromoCodeSuccess('');
        }, 5000);
      } else {
        setPromoCodeError(result.message || 'Invalid promotional code');
      }
    } catch (error) {
      console.error('Error validating code:', error);
      setPromoCodeError(error.response?.data?.error || 'Failed to validate code. Please check the event ID and code.');
    } finally {
      setValidatingCode(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatMessageDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days === 0) {
      return date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return 'Yesterday';
    } else if (days < 7) {
      return date.toLocaleDateString('en-AU', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
    }
  };

  const isUpcoming = (dateString) => {
    return new Date(dateString) >= new Date();
  };

  if (!user) {
    return (
      <div className="profile-container">
        <div className="profile-login-required">
          <UserIcon size={64} />
          <h2>Login Required</h2>
          <p>Please log in to view your profile.</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'details', label: 'My Details', icon: UserIcon },
    { id: 'messages', label: 'Messages', icon: MessageCircle },
    { id: 'tickets', label: 'Tickets', icon: Ticket },
    { id: 'orders', label: 'Orders', icon: ShoppingBag },
    { id: 'photos', label: 'Photos', icon: Camera }
  ];

  return (
    <div className="profile-container">
      <div className="profile-header">
        <h1>My Profile</h1>
        <div className="user-info">
          <p>Welcome, {user.firstName || user.name} {user.lastName}</p>
          <p className="user-email">{user.email}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="profile-tabs">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              className={`profile-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <Icon size={20} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="profile-content">
        {/* My Details Tab */}
        {activeTab === 'details' && (
          <div className="profile-tab-content">
            <h2>My Details</h2>
            <div className="profile-details-card">
              <div className="profile-detail-item">
                <strong>Name:</strong>
                <span>{user.firstName || user.name} {user.lastName}</span>
              </div>
              <div className="profile-detail-item">
                <strong>Email:</strong>
                <span>{user.email}</span>
              </div>
              {user.phone && (
                <div className="profile-detail-item">
                  <strong>Phone:</strong>
                  <span>{user.phone}</span>
                </div>
              )}
              <div className="profile-actions">
                <a href="/scanner" className="btn btn-orange">
                  <QrCode size={18} />
                  Ticket Scanner
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <div className="profile-tab-content profile-messages-content">
            <div className="messages-header-actions">
              <h2>Messages</h2>
              <button onClick={() => setShowComposer(true)} className="btn btn-orange">
                <Send size={20} />
                New Message
              </button>
            </div>

            {/* Promotional Code Entry Section */}
            <div className="promo-code-section">
              <div className="promo-code-header">
                <Key size={24} />
                <h3>Enter Secret Event Password</h3>
              </div>
              <p className="promo-code-description">
                Received a secret password for an event in a message? Enter it here to gain access to the event.
              </p>
              <form onSubmit={handleValidatePromoCode} className="promo-code-form">
                <div className="promo-code-fields">
                  <div className="promo-code-field">
                    <label htmlFor="promoEventId">Event ID *</label>
                    <input
                      type="text"
                      id="promoEventId"
                      value={promoEventId}
                      onChange={(e) => {
                        setPromoEventId(e.target.value);
                        setPromoCodeError('');
                      }}
                      placeholder="Event ID (from message)"
                      required
                    />
                  </div>
                  <div className="promo-code-field">
                    <label htmlFor="promoCode">Secret Password *</label>
                    <input
                      type="text"
                      id="promoCode"
                      value={promoCode}
                      onChange={(e) => {
                        setPromoCode(e.target.value);
                        setPromoCodeError('');
                      }}
                      placeholder="Enter secret password"
                      required
                    />
                  </div>
                </div>
                {promoCodeError && (
                  <div className="promo-code-error">{promoCodeError}</div>
                )}
                {promoCodeSuccess && (
                  <div className="promo-code-success">{promoCodeSuccess}</div>
                )}
                <button type="submit" className="btn btn-orange" disabled={validatingCode}>
                  <Lock size={18} />
                  {validatingCode ? 'Validating...' : 'Validate Password'}
                </button>
              </form>
            </div>
            
            <div className="messages-layout">
              <div className="messages-sidebar">
                <div className="messages-sidebar-header">
                  <Inbox size={20} />
                  <h3>Conversations</h3>
                </div>
                {messagesLoading ? (
                  <div className="messages-loading">Loading conversations...</div>
                ) : conversations.length > 0 ? (
                  <div className="messages-conversations-list">
                    {conversations.map((conversation) => (
                      <div
                        key={conversation.id}
                        className={`messages-conversation-item ${
                          selectedConversation?.id === conversation.id ? 'active' : ''
                        }`}
                        onClick={() => setSelectedConversation(conversation)}
                      >
                        <div className="messages-conversation-avatar">
                          <UserIcon size={20} />
                        </div>
                        <div className="messages-conversation-info">
                          <div className="messages-conversation-header">
                            <span className="messages-conversation-name">
                              {conversation.otherUserName || 'Unknown User'}
                            </span>
                            {conversation.unreadCount > 0 && (
                              <span className="messages-unread-badge">
                                {conversation.unreadCount}
                              </span>
                            )}
                          </div>
                          <p className="messages-conversation-preview">
                            {conversation.lastMessage?.substring(0, 50) || 'No messages yet'}
                            {conversation.lastMessage?.length > 50 ? '...' : ''}
                          </p>
                          {conversation.lastMessageDate && (
                            <span className="messages-conversation-date">
                              {formatMessageDate(conversation.lastMessageDate)}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="messages-empty">
                    <p>No conversations yet</p>
                    <button onClick={() => setShowComposer(true)} className="btn btn-small">
                      Start a conversation
                    </button>
                  </div>
                )}
              </div>

              <div className="messages-main">
                {selectedConversation ? (
                  <>
                    <div className="messages-thread-header">
                      <div className="messages-thread-user">
                        <UserIcon size={24} />
                        <h3>{selectedConversation.otherUserName || 'Unknown User'}</h3>
                      </div>
                    </div>
                    <div className="messages-thread">
                      {messages.length > 0 ? (
                        messages.map((message) => (
                          <div
                            key={message.id}
                            className={`messages-message ${
                              message.senderId === user.id ? 'sent' : 'received'
                            }`}
                          >
                            <div className="messages-message-content">
                              {message.subject && (
                                <div className="messages-message-subject">{message.subject}</div>
                              )}
                              <p>{message.body}</p>
                              <span className="messages-message-time">
                                {formatMessageDate(message.createdAt)}
                              </span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="messages-empty-thread">
                          <p>No messages in this conversation yet</p>
                        </div>
                      )}
                    </div>
                    <div className="messages-compose">
                      <form onSubmit={handleSendMessage}>
                        <textarea
                          placeholder="Type your message..."
                          value={newMessage.body}
                          onChange={(e) => setNewMessage({ ...newMessage, body: e.target.value })}
                          rows="3"
                          required
                        />
                        <button type="submit" className="btn btn-orange" disabled={sending}>
                          <Send size={18} />
                          {sending ? 'Sending...' : 'Send'}
                        </button>
                      </form>
                    </div>
                  </>
                ) : (
                  <div className="messages-select-conversation">
                    <MessageCircle size={64} />
                    <h3>Select a conversation</h3>
                    <p>Choose a conversation from the list to view messages</p>
                  </div>
                )}
              </div>
            </div>

            {/* New Message Composer Modal */}
            {showComposer && (
              <div className="messages-modal-overlay" onClick={() => setShowComposer(false)}>
                <div className="messages-modal" onClick={(e) => e.stopPropagation()}>
                  <h2>New Message</h2>
                  <form onSubmit={handleSendMessage}>
                    <div className="messages-form-group">
                      <label htmlFor="recipient">Recipient User ID *</label>
                      <input
                        type="text"
                        id="recipient"
                        value={newMessage.recipientId}
                        onChange={(e) => setNewMessage({ ...newMessage, recipientId: e.target.value })}
                        placeholder="Enter user ID"
                        required
                      />
                      <small>Note: In a full implementation, this would be a user selector</small>
                    </div>
                    <div className="messages-form-group">
                      <label htmlFor="subject">Subject (optional)</label>
                      <input
                        type="text"
                        id="subject"
                        value={newMessage.subject}
                        onChange={(e) => setNewMessage({ ...newMessage, subject: e.target.value })}
                        placeholder="Message subject"
                      />
                    </div>
                    <div className="messages-form-group">
                      <label htmlFor="body">Message *</label>
                      <textarea
                        id="body"
                        value={newMessage.body}
                        onChange={(e) => setNewMessage({ ...newMessage, body: e.target.value })}
                        placeholder="Type your message..."
                        rows="5"
                        required
                      />
                    </div>
                    <div className="messages-modal-actions">
                      <button
                        type="button"
                        onClick={() => setShowComposer(false)}
                        className="btn btn-secondary"
                      >
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-orange" disabled={sending}>
                        {sending ? 'Sending...' : 'Send Message'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tickets Tab */}
        {activeTab === 'tickets' && (
          <div className="profile-tab-content">
            <h2>My Tickets</h2>
            {ticketsLoading ? (
              <div className="profile-loading">Loading your tickets...</div>
            ) : tickets.length === 0 ? (
              <div className="no-tickets">
                <p>You don't have any tickets yet.</p>
                <p>Purchase tickets to events to see them here!</p>
              </div>
            ) : (
              <div className="tickets-grid">
                {tickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    className={`ticket-card ${isUpcoming(ticket.event.date) ? 'upcoming' : 'past'}`}
                  >
                    <div className="ticket-header">
                      <h3>{ticket.event.name}</h3>
                      <span className={`ticket-status ${ticket.status}`}>
                        {ticket.status === 'valid' ? '✓ Valid' : ticket.status}
                      </span>
                    </div>
                    <div className="ticket-details">
                      <p className="ticket-date">📅 {formatDate(ticket.event.date)}</p>
                      {ticket.event.location && (
                        <p className="ticket-location">📍 {ticket.event.location}</p>
                      )}
                      <p className="ticket-number">Ticket #{ticket.ticketNumber}</p>
                    </div>
                    {isUpcoming(ticket.event.date) && ticket.status === 'valid' && (
                      <div className="ticket-actions">
                        <button onClick={() => loadTicketQRCode(ticket.id)} className="btn-show-qr">
                          Show QR Code
                        </button>
                      </div>
                    )}
                    {ticket.status === 'used' && (
                      <div className="ticket-used">
                        <p>✓ Already scanned at event</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {selectedTicket && (
              <div className="qr-modal-overlay" onClick={() => setSelectedTicket(null)}>
                <div className="qr-modal" onClick={(e) => e.stopPropagation()}>
                  <button className="qr-modal-close" onClick={() => setSelectedTicket(null)}>×</button>
                  <div className="qr-modal-content">
                    <h2>{selectedTicket.eventName}</h2>
                    <p className="qr-event-date">{formatDate(selectedTicket.eventDate)}</p>
                    <p className="qr-ticket-number">Ticket #{selectedTicket.ticketNumber}</p>
                    <div className="qr-code-container">
                      {selectedTicket.qrCodeImage ? (
                        <img src={selectedTicket.qrCodeImage} alt="Ticket QR Code" className="qr-code-image" />
                      ) : (
                        <div className="qr-loading">Loading QR code...</div>
                      )}
                    </div>
                    <p className="qr-instructions">Show this QR code at the event entrance for scanning</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="profile-tab-content">
            <h2>My Orders</h2>
            {ordersLoading ? (
              <div className="profile-loading">Loading your orders...</div>
            ) : orders.length === 0 ? (
              <div className="no-orders">
                <ShoppingBag size={64} />
                <p>You don't have any orders yet.</p>
                <p>Purchase products to see your orders here!</p>
                <a href="/products" className="btn btn-orange">
                  Browse Products
                </a>
              </div>
            ) : (
              <div className="orders-list">
                {orders.map((order) => (
                  <div key={order.id} className="order-card">
                    <div className="order-header">
                      <h3>Order #{order.orderNumber || order.id}</h3>
                      <span className={`order-status ${order.status}`}>
                        {order.status}
                      </span>
                    </div>
                    <div className="order-details">
                      <p>Date: {formatDate(order.createdAt)}</p>
                      <p>Total: ${order.totalAmount?.toFixed(2) || '0.00'}</p>
                      {order.items && order.items.length > 0 && (
                        <div className="order-items">
                          <strong>Items:</strong>
                          <ul>
                            {order.items.map((item, index) => (
                              <li key={index}>
                                {item.quantity}x {item.productName || item.name}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Photos Tab */}
        {activeTab === 'photos' && (
          <div className="profile-tab-content">
            <h2>My Photos</h2>
            <div className="photos-info-card">
              <Camera size={64} />
              <h3>Face Recognition Photo Detection</h3>
              <p>
                Upload a photo of yourself to your profile, and our AI will automatically detect 
                you in event photos!
              </p>
              <p>
                When photos are uploaded from events you attended, you'll receive notifications 
                with links to all the photos you appear in.
              </p>
              <div className="photos-features">
                <div className="photos-feature">
                  <strong>✨ How it works:</strong>
                  <ul>
                    <li>Upload your profile photo</li>
                    <li>Attend events and get your photo taken</li>
                    <li>We'll detect you in event photos automatically</li>
                    <li>Get notified with links to your photos</li>
                  </ul>
                </div>
              </div>
              <div className="photos-actions">
                {user.profilePhoto ? (
                  <div className="profile-photo-preview">
                    <img src={user.profilePhoto} alt="Your profile photo" />
                    <p>Profile photo uploaded ✓</p>
                  </div>
                ) : (
                  <div className="photos-upload-placeholder">
                    <p>Upload a profile photo to enable face recognition</p>
                    <button className="btn btn-orange" disabled>
                      Upload Photo (Coming Soon)
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
